import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AlertTriangle, Zap, CreditCard, Loader2 } from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const QUICK_OPTIONS = [
  { id: "100", credits: 100, price: 10 },
  { id: "250", credits: 250, price: 20 },
  { id: "500", credits: 500, price: 35 }
];

const InsufficientCreditsModal = ({ 
  open, 
  onOpenChange, 
  currentCredits, 
  requiredCredits, 
  actionName = "this action"
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState("250");
  
  const shortfall = Math.max(0, requiredCredits - currentCredits);

  const handleQuickBuy = async (packageId) => {
    setLoading(true);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API_URL}/api/checkout/quick-topup`,
        {
          package_id: packageId,
          origin_url: originUrl
        },
        { withCredentials: true }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate checkout');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <DialogTitle className="text-lg">Insufficient Credits</DialogTitle>
              <DialogDescription>
                You need {shortfall} more credit{shortfall !== 1 ? 's' : ''} for {actionName}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700 font-medium">Current Balance</p>
              <p className="text-2xl font-bold text-gray-900">{currentCredits} credits</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-700 font-medium">Required</p>
              <p className="text-2xl font-bold text-orange-700">{requiredCredits} credits</p>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <p className="text-sm font-semibold text-gray-900 mb-3">Quick Top-Up Options:</p>
          <div className="space-y-2">
            {QUICK_OPTIONS.map((option) => (
              <Card
                key={option.id}
                className={`p-3 cursor-pointer transition-all hover:shadow-md ${
                  selectedOption === option.id ? 'border-2 border-indigo-600 bg-indigo-50' : ''
                }`}
                onClick={() => setSelectedOption(option.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-indigo-600" />
                    <span className="font-semibold text-gray-900">{option.credits} credits</span>
                  </div>
                  <span className="text-lg font-bold text-indigo-700">${option.price}</span>
                </div>
              </Card>
            ))}
          </div>
          <div className="mt-4 text-center">
            <a 
              href="/pricing" 
              className="text-sm text-indigo-600 hover:text-indigo-700 font-semibold underline"
              onClick={() => onOpenChange(false)}
            >
              💎 Subscribe to Save - Get more for less!
            </a>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            onClick={() => handleQuickBuy(selectedOption)}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Buy Credits
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InsufficientCreditsModal;
